// src/layouts/MainLayout.jsx
import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useTheme,
  alpha,
  InputBase,
  Chip,
  Stack,
  LinearProgress,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  PhotoCamera,
  Receipt,
  Description,
  People,
  Folder,
  Settings,
  Notifications,
  AccountCircle,
  Logout,
  ChevronLeft,
  ChevronRight,
  DarkMode,
  LightMode,
  Assessment,
  Article,
  Search,
  Close,
  KeyboardArrowDown,
  Help,
  Feedback,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../app/slice/authSlice";
import { useNavigate as useRouterNavigate } from "react-router-dom";

const drawerWidth = 260;
const collapsedDrawerWidth = 65;

const MainLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { user } = useSelector((state) => state.auth);

  const menuItems = [
    { text: "Dashboard", icon: <Dashboard />, path: "/dashboard", badge: null },
    // { text: "Blog", icon: <Article />, path: "/blog", badge: "3" },
    {
      text: "Private Albums",
      icon: <PhotoCamera />,
      path: "/private-albums",
      badge: "12",
    },
    { text: "Invoices", icon: <Receipt />, path: "/invoices", badge: "5" },
    {
      text: "Public Galleries",
      icon: <PhotoCamera />,
      path: "/public-galleries",
      badge: "8",
    },
    {
      text: "Invitations",
      icon: <Description />,
      path: "/invitations",
      badge: "2",
    },
    {
      text: "CRM",
      icon: <People />,
      path: "/crm",
      badge: "4",
    },
    {
      text: "Accounts",
      icon: <Assessment />,
      path: "/accounts",
      badge: null,
    },
    {
      text: "Quotations",
      icon: <Description />,
      path: "/quotations",
      badge: "3",
    },
    // { text: "Clients", icon: <People />, path: "/clients", badge: null },
    // { text: "Files", icon: <Folder />, path: "/files", badge: null },
    // { text: "Reports", icon: <Assessment />, path: "/reports", badge: null },
    { text: "Events", icon: <Assessment />, path: "/events", badge: null },
  ];

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
    handleMenuClose();
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results or handle search
      console.log("Searching for:", searchQuery);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: darkMode
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          {/* Menu Toggle */}
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              mr: 2,
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "rotate(90deg)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: "0.5px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: { xs: "none", sm: "inline" },
              }}
            >
              Hashtag Photography
            </Box>
            <Box
              component="span"
              sx={{
                display: { xs: "inline", sm: "none" },
              }}
            >
              HP
            </Box>
            <Chip
              label="Admin"
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                height: 20,
                fontSize: "0.65rem",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          </Typography>

          {/* Search Bar */}
          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.25)",
              },
              mr: 2,
              display: { xs: "none", md: "flex" },
              minWidth: searchOpen ? 300 : 200,
              transition: "min-width 0.3s ease",
            }}
          >
            <Box
              sx={{
                p: 1,
                height: "100%",
                position: "absolute",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Search sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
            </Box>
            <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
              <InputBase
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  color: "white",
                  width: "100%",
                  "& .MuiInputBase-input": {
                    p: 1,
                    pl: 4,
                    width: "100%",
                    "&::placeholder": {
                      color: "rgba(255,255,255,0.7)",
                      opacity: 1,
                    },
                  },
                }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setSearchOpen(false)}
              />
            </form>
            {searchQuery && (
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  right: 4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "rgba(255,255,255,0.7)",
                }}
                onClick={() => setSearchQuery("")}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton color="inherit" onClick={handleNotifOpen}>
              <Badge badgeContent={4} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={notifAnchorEl}
            open={Boolean(notifAnchorEl)}
            onClose={handleNotifClose}
            PaperProps={{
              sx: {
                width: 340,
                maxHeight: 400,
                borderRadius: 2,
                mt: 1,
                boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Notifications
              </Typography>
            </Box>
            <MenuItem>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  New blog comment
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  John commented on "Wedding Photography Tips"
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  5 min ago
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Invoice #INV-0001 is overdue
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Payment is 5 days overdue
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  1 hour ago
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  New quotation request
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wedding photography request from Sarah
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  2 hours ago
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleNotifClose}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ width: "100%", textAlign: "center" }}
              >
                View all notifications
              </Typography>
            </MenuItem>
          </Menu>

          {/* Theme Toggle */}
          <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"}>
            <IconButton
              color="inherit"
              onClick={() => setDarkMode(!darkMode)}
              sx={{
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              {darkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>

          {/* Profile */}
          <IconButton color="inherit" onClick={handleMenuOpen} sx={{ ml: 1 }}>
            <Avatar
              sx={{
                bgcolor: "#764ba2",
                width: 36,
                height: 36,
                border: "2px solid rgba(255,255,255,0.3)",
                transition: "border-color 0.3s ease",
                "&:hover": {
                  borderColor: "white",
                },
              }}
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Profile"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                getInitials(user?.displayName || user?.email)
              )}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                width: 220,
                borderRadius: 2,
                mt: 1,
                boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
              <Typography variant="body2" fontWeight="bold">
                {user?.displayName || "User"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email || "user@email.com"}
              </Typography>
            </Box>
            <MenuItem onClick={handleMenuClose}>
              <AccountCircle sx={{ mr: 1 }} /> Profile
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <Settings sx={{ mr: 1 }} /> Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleMenuClose}>
              <Help sx={{ mr: 1 }} /> Help
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <Feedback sx={{ mr: 1 }} /> Feedback
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? drawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedDrawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            backgroundColor: darkMode ? "#1a1a2e" : "#ffffff",
            borderRight: "1px solid",
            borderColor: darkMode
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.06)",
            boxShadow: "2px 0 12px rgba(0,0,0,0.05)",
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: "auto", mt: 1 }}>
          <List>
            {menuItems.map((item) => (
              <Tooltip
                key={item.text}
                title={!open ? item.text : ""}
                placement="right"
              >
                <ListItem
                  button
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    mb: 0.5,
                    px: open ? 2 : 1.5,
                    py: 1.2,
                    minHeight: 48,
                    justifyContent: open ? "flex-start" : "center",
                    backgroundColor: isActive(item.path)
                      ? darkMode
                        ? "rgba(102, 126, 234, 0.2)"
                        : "rgba(102, 126, 234, 0.12)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isActive(item.path)
                        ? darkMode
                          ? "rgba(102, 126, 234, 0.3)"
                          : "rgba(102, 126, 234, 0.18)"
                        : darkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                    },
                    "&::before": isActive(item.path)
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 4,
                          height: 28,
                          backgroundColor: "#667eea",
                          borderRadius: "0 4px 4px 0",
                        }
                      : {},
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive(item.path)
                        ? "#667eea"
                        : darkMode
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(0,0,0,0.5)",
                      minWidth: open ? 40 : 0,
                      justifyContent: "center",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", flex: 1 }}
                    >
                      <ListItemText
                        primary={item.text}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "0.875rem",
                            fontWeight: isActive(item.path) ? 600 : 400,
                            color: isActive(item.path)
                              ? "#667eea"
                              : darkMode
                                ? "rgba(255,255,255,0.85)"
                                : "rgba(0,0,0,0.85)",
                            transition: "color 0.2s ease",
                          },
                        }}
                      />
                      {item.badge && (
                        <Chip
                          label={item.badge}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            bgcolor: isActive(item.path)
                              ? "#667eea"
                              : "#ff4d4f",
                            color: "white",
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      )}
                    </Box>
                  )}
                </ListItem>
              </Tooltip>
            ))}
          </List>
        </Box>

        {/* Drawer Bottom */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
          <Divider
            sx={{
              borderColor: darkMode
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
            }}
          />

          {/* User Info in Drawer (when open) */}
          {open && (
            <Box sx={{ px: 2, py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "#667eea",
                  }}
                >
                  {getInitials(user?.displayName || user?.email)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {user?.displayName || "User"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user?.email || "user@email.com"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Toggle Button */}
          <ListItem
            button
            onClick={handleDrawerToggle}
            sx={{
              justifyContent: "center",
              py: 1.5,
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            <IconButton size="small">
              {open ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </ListItem>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            sm: `calc(100% - ${open ? drawerWidth : collapsedDrawerWidth}px)`,
          },
          mt: 8,
          backgroundColor: darkMode ? "#121212" : "#f5f7fa",
          minHeight: "100vh",
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
